(() => {
  const DESKTOP_QUERY = '(min-width: 750px)';
  const productDocumentCache = new Map();

  const initialize = (section) => {
    if (!section || section.dataset.minaCollectionReady === 'true') return;
    section.dataset.minaCollectionReady = 'true';

    const overlay = section.querySelector('[data-filter-overlay]');
    const drawer = section.querySelector('[data-filter-drawer]');
    const openButton = section.querySelector('[data-filter-open]');
    const closeButton = section.querySelector('[data-filter-close]');
    const sortSelect = section.querySelector('[data-sort-select]');
    const sortMenu = section.querySelector('[data-sort-menu]');
    const sortTrigger = section.querySelector('[data-sort-trigger]');
    const sortOptions = section.querySelector('[data-sort-options]');
    const sortCurrent = section.querySelector('[data-sort-current]');
    const sortOptionButtons = [...section.querySelectorAll('[data-sort-option]')];
    let lastFocusedElement = null;

    const initializeProductCardSwatches = (card) => {
      if (!card || card.dataset.minaProductSwatchesReady === 'true') return;

      const previewImage = card.querySelector('[data-product-card-color-preview]');
      const swatchGroup = card.querySelector('[data-product-card-swatches]');
      const swatches = [...card.querySelectorAll('[data-product-card-swatch]')];
      const productLinks = [...card.querySelectorAll('[data-product-card-link]')];

      if (!previewImage || !swatchGroup || !swatches.length) return;
      card.dataset.minaProductSwatchesReady = 'true';

      const baseLinks = productLinks.map((link) => link.getAttribute('href'));
      let loadToken = 0;

      const updateProductLinks = (variantUrl = '') => {
        productLinks.forEach((link, index) => {
          link.setAttribute('href', variantUrl || baseLinks[index] || link.getAttribute('href'));
        });
      };

      const setPressedState = (activeSwatch) => {
        swatches.forEach((swatch) => {
          swatch.setAttribute('aria-pressed', String(swatch === activeSwatch));
        });
      };

      const hidePreview = () => {
        loadToken += 1;
        previewImage.classList.remove('is-visible');
        card.classList.remove('is-color-previewing');
      };

      const showPreview = (swatch) => {
        const imageUrl = swatch?.dataset.swatchImage;
        if (!imageUrl) {
          hidePreview();
          return;
        }

        const token = ++loadToken;
        previewImage.classList.remove('is-visible');
        card.classList.add('is-color-previewing');

        previewImage.alt = swatch.dataset.swatchAlt || '';
        previewImage.srcset = swatch.dataset.swatchSrcset || '';
        previewImage.src = imageUrl;

        const reveal = () => {
          if (token !== loadToken) return;
          window.requestAnimationFrame(() => {
            if (token !== loadToken) return;
            previewImage.classList.add('is-visible');
          });
        };

        if (previewImage.complete) {
          reveal();
        } else {
          previewImage.addEventListener('load', reveal, { once: true });
        }
      };

      const restoreBaseImage = () => {
        hidePreview();
        setPressedState(null);
        updateProductLinks();
      };

      swatches.forEach((swatch) => {
        swatch.addEventListener('mouseenter', () => showPreview(swatch));
        swatch.addEventListener('focus', () => showPreview(swatch));

        swatch.addEventListener('click', () => {
          setPressedState(swatch);
          showPreview(swatch);
          updateProductLinks(swatch.dataset.variantUrl || '');
        });

        swatch.addEventListener('blur', () => {
          window.requestAnimationFrame(() => {
            if (!swatchGroup.contains(document.activeElement)) restoreBaseImage();
          });
        });
      });

      swatchGroup.addEventListener('mouseleave', restoreBaseImage);
    };

    section.querySelectorAll('[data-mina-product-card]').forEach(initializeProductCardSwatches);

    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

    const closeDrawer = () => {
      if (!overlay || !overlay.classList.contains('is-open')) return;
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      openButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      lastFocusedElement?.focus();
    };

    const openDrawer = () => {
      if (!overlay) return;
      lastFocusedElement = document.activeElement;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      openButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      window.requestAnimationFrame(() => closeButton?.focus());
    };

    openButton?.addEventListener('click', openDrawer);
    closeButton?.addEventListener('click', closeDrawer);

    overlay?.addEventListener('click', (event) => {
      if (event.target === overlay) closeDrawer();
    });

    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll(focusableSelector)].filter(
        (element) => element.offsetParent !== null,
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const closeSortMenu = ({ returnFocus = false } = {}) => {
      if (!sortOptions?.classList.contains('is-open')) return;
      sortOptions.classList.remove('is-open');
      sortOptions.setAttribute('aria-hidden', 'true');
      sortTrigger?.setAttribute('aria-expanded', 'false');
      if (returnFocus) sortTrigger?.focus();
    };

    const openSortMenu = () => {
      if (!sortOptions) return;
      sortOptions.classList.add('is-open');
      sortOptions.setAttribute('aria-hidden', 'false');
      sortTrigger?.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => {
        const selectedOption = sortOptions.querySelector('[aria-selected="true"]');
        (selectedOption || sortOptionButtons[0])?.focus();
      });
    };

    sortTrigger?.addEventListener('click', () => {
      if (sortOptions?.classList.contains('is-open')) {
        closeSortMenu();
      } else {
        openSortMenu();
      }
    });

    sortOptionButtons.forEach((optionButton) => {
      optionButton.addEventListener('click', () => {
        if (!sortSelect) return;
        sortSelect.value = optionButton.dataset.sortValue || '';
        if (sortCurrent) sortCurrent.textContent = optionButton.textContent.trim();
        sortOptionButtons.forEach((button) => {
          const isSelected = button === optionButton;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-selected', String(isSelected));
        });
        closeSortMenu();
        sortSelect.form?.requestSubmit();
      });
    });

    sortMenu?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSortMenu({ returnFocus: true });
      }
    });

    document.addEventListener('click', (event) => {
      if (sortMenu && !sortMenu.contains(event.target)) closeSortMenu();
    });

    /* ------------------------------------------------------------
       Desktop Quick Buy
       ------------------------------------------------------------ */
    const quickBuy = section.querySelector('[data-mina-quickbuy]');
    if (quickBuy) {
      const surface = quickBuy.querySelector('[data-mina-quickbuy-surface]');
      const closeQuickBuyButton = quickBuy.querySelector('[data-mina-quickbuy-close]');
      const stage = quickBuy.querySelector('[data-mina-quickbuy-stage]');
      const mediaNav = quickBuy.querySelector('[data-mina-quickbuy-media-nav]');
      const title = quickBuy.querySelector('[data-mina-quickbuy-title]');
      const price = quickBuy.querySelector('[data-mina-quickbuy-price]');
      const saveButton = quickBuy.querySelector('[data-mina-quickbuy-save]');
      const optionsWrap = quickBuy.querySelector('[data-mina-quickbuy-options]');
      const sizeChartButton = quickBuy.querySelector('[data-mina-quickbuy-size-chart]');
      const checkoutButton = quickBuy.querySelector('[data-mina-quickbuy-checkout]');
      const addButton = quickBuy.querySelector('[data-mina-quickbuy-add]');
      const detailsLink = quickBuy.querySelector('[data-mina-quickbuy-details]');
      const status = quickBuy.querySelector('[data-mina-quickbuy-status]');

      const state = {
        opener: null,
        productUrl: '',
        sourceDocument: null,
        productRoot: null,
        variants: [],
        priceByVariant: new Map(),
        selectedOptions: [],
        optionGroups: [],
        galleryKey: '__fallback',
        mediaSlides: [],
        mediaIndex: 0,
        mediaAnimating: false,
        pendingMediaIndex: null,
        activeVariant: null,
        sizeChartClone: null,
        requestToken: 0,
        busy: false,
      };

      const resetStatus = () => {
        if (status) status.textContent = '';
      };

      const setBusy = (busy) => {
        state.busy = busy;
        quickBuy.classList.toggle('is-busy', busy);
        if (checkoutButton) checkoutButton.disabled = busy || !state.activeVariant?.available;
        if (addButton) addButton.disabled = busy || !state.activeVariant?.available;
      };

      const loadProductDocument = async (url) => {
        const absolute = new URL(url, window.location.origin).href;
        if (productDocumentCache.has(absolute)) return productDocumentCache.get(absolute);

        const promise = fetch(absolute, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Unable to load product (${response.status})`);
            return response.text();
          })
          .then((html) => new DOMParser().parseFromString(html, 'text/html'))
          .catch((error) => {
            productDocumentCache.delete(absolute);
            throw error;
          });

        productDocumentCache.set(absolute, promise);
        return promise;
      };

      const extractOptionGroups = (root) => {
        return [...root.querySelectorAll('[data-option-position]')].map((fieldset) => {
          const position = Number(fieldset.dataset.optionPosition) || 1;
          const legend = fieldset.querySelector('legend')?.textContent.trim() || `Option ${position}`;
          const isColor = fieldset.classList.contains('minaco-product__option--color');
          const isSize = fieldset.classList.contains('minaco-product__option--size');
          const values = [...fieldset.querySelectorAll('[data-option-value]')].map((input) => {
            const label = root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            const dot = label?.querySelector('.minaco-product__swatch-dot');
            const styleSource = label?.getAttribute('style') || '';
            let swatchImage = '';
            let swatchColor = '';
            const imageMatch = styleSource.match(/--mp-swatch-image:\s*url\((['"]?)(.*?)\1\)/i);
            const colorMatch = styleSource.match(/--mp-swatch-color:\s*([^;]+)/i);
            if (imageMatch) swatchImage = imageMatch[2];
            if (colorMatch) swatchColor = colorMatch[1].trim();

            return {
              value: input.value,
              selected: input.checked,
              galleryKey: input.dataset.colorGalleryKey || '',
              swatchImage,
              swatchColor,
              label: (label?.textContent || input.value).trim(),
              hasDot: Boolean(dot),
            };
          });

          return { position, legend, isColor, isSize, values };
        });
      };

      const getSlidesForKey = (key) => {
        if (!state.productRoot) return [];
        const all = [...state.productRoot.querySelectorAll('[data-minaco-product-slide]')]
          .filter((slide) => !slide.hasAttribute('data-minaco-carousel-clone'));
        let slides = all.filter((slide) => slide.dataset.galleryKey === key);
        if (!slides.length) slides = all.filter((slide) => slide.dataset.galleryKey === '__fallback');
        if (!slides.length) slides = all;
        return slides.slice(0, 6);
      };

      const mediaElementFromSource = (sourceSlide) => {
        const video = sourceSlide.querySelector('video');
        if (video) {
          const clone = video.cloneNode(true);
          clone.className = 'mina-quickbuy__media';
          clone.controls = true;
          clone.muted = true;
          clone.loop = true;
          clone.playsInline = true;
          clone.preload = 'metadata';
          return clone;
        }

        const image = sourceSlide.querySelector('img');
        if (image) {
          const clone = image.cloneNode(true);
          clone.className = 'mina-quickbuy__media';
          clone.loading = 'eager';
          clone.removeAttribute('fetchpriority');
          return clone;
        }

        const svg = sourceSlide.querySelector('svg');
        if (svg) return svg.cloneNode(true);
        return document.createElement('div');
      };

      const pauseFrames = (except = null) => {
        stage?.querySelectorAll('video').forEach((video) => {
          if (except && except.contains(video)) return;
          video.pause();
        });
      };

      const playFrame = (frame) => {
        const video = frame?.querySelector('video');
        if (video) video.play().catch(() => {});
      };

      const updateThumbs = () => {
        mediaNav?.querySelectorAll('[data-mina-quickbuy-thumb]').forEach((button, index) => {
          const active = index === state.mediaIndex;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-current', active ? 'true' : 'false');
        });
      };

      const createFrame = (sourceSlide) => {
        const frame = document.createElement('div');
        frame.className = 'mina-quickbuy__media-frame';
        frame.append(mediaElementFromSource(sourceSlide));
        return frame;
      };

      const showMedia = (index, { animate = true, force = false, directionOverride = 0 } = {}) => {
        if (!stage || !state.mediaSlides.length) return;
        const total = state.mediaSlides.length;
        const target = Math.max(0, Math.min(total - 1, index));

        if (state.mediaAnimating) {
          state.pendingMediaIndex = target;
          return;
        }

        const currentFrame = stage.querySelector('.mina-quickbuy__media-frame');
        if (!animate || !currentFrame || (target === state.mediaIndex && !force)) {
          stage.replaceChildren(createFrame(state.mediaSlides[target]));
          state.mediaIndex = target;
          updateThumbs();
          playFrame(stage.firstElementChild);
          return;
        }

        state.mediaAnimating = true;
        state.pendingMediaIndex = null;
        const direction = directionOverride || (target >= state.mediaIndex ? 1 : -1);
        const incoming = createFrame(state.mediaSlides[target]);
        incoming.classList.add(direction > 0 ? 'is-entering-from-right' : 'is-entering-from-left');
        stage.append(incoming);
        pauseFrames();

        // Two frames guarantee the browser paints the off-screen start position first.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            currentFrame.classList.add('is-animating', direction > 0 ? 'is-leaving-left' : 'is-leaving-right');
            incoming.classList.add('is-animating');
            incoming.classList.remove('is-entering-from-right', 'is-entering-from-left');
          });
        });

        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          currentFrame.remove();
          incoming.classList.remove('is-animating');
          state.mediaIndex = target;
          state.mediaAnimating = false;
          updateThumbs();
          playFrame(incoming);
          if (state.pendingMediaIndex !== null && state.pendingMediaIndex !== state.mediaIndex) {
            const pending = state.pendingMediaIndex;
            state.pendingMediaIndex = null;
            showMedia(pending, { animate: true });
          }
        };

        incoming.addEventListener('transitionend', finish, { once: true });
        window.setTimeout(finish, 620);
      };

      const buildThumb = (slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mina-quickbuy__thumb';
        button.dataset.minaQuickbuyThumb = '';
        button.setAttribute('aria-label', `Show media ${index + 1}`);

        const sourceImage = slide.querySelector('img');
        const sourceVideo = slide.querySelector('video');
        if (sourceImage) {
          const thumb = sourceImage.cloneNode(true);
          thumb.removeAttribute('srcset');
          thumb.removeAttribute('sizes');
          thumb.loading = 'lazy';
          button.append(thumb);
        } else if (sourceVideo) {
          if (sourceVideo.poster) {
            const thumb = document.createElement('img');
            thumb.src = sourceVideo.poster;
            thumb.alt = '';
            thumb.loading = 'lazy';
            button.append(thumb);
          }
          const mark = document.createElement('span');
          mark.className = 'mina-quickbuy__thumb-video-mark';
          mark.textContent = '▶';
          mark.setAttribute('aria-hidden', 'true');
          button.append(mark);
        }

        button.addEventListener('click', () => showMedia(index, { animate: true }));
        return button;
      };

      const renderGallery = (key, { animateFirst = false } = {}) => {
        state.galleryKey = key || '__fallback';
        state.mediaSlides = getSlidesForKey(state.galleryKey);
        state.mediaIndex = 0;
        state.mediaAnimating = false;
        state.pendingMediaIndex = null;
        mediaNav?.replaceChildren();

        state.mediaSlides.forEach((slide, index) => mediaNav?.append(buildThumb(slide, index)));
        if (mediaNav) mediaNav.hidden = state.mediaSlides.length <= 1;
        if (state.mediaSlides.length) showMedia(0, { animate: animateFirst, force: animateFirst, directionOverride: animateFirst ? 1 : 0 });
        else stage?.replaceChildren();
      };

      const currentGalleryKey = () => {
        const colorGroup = state.optionGroups.find((group) => group.isColor);
        if (!colorGroup) return '__fallback';
        const selected = colorGroup.values.find((value) => state.selectedOptions[colorGroup.position - 1] === value.value);
        return selected?.galleryKey || '__fallback';
      };

      const resolveVariant = () => {
        if (!state.optionGroups.length) {
          return state.variants.find((variant) => variant.available) || state.variants[0] || null;
        }
        return state.variants.find((variant) =>
          variant.options.every((value, index) => value === state.selectedOptions[index]),
        ) || null;
      };

      const updateOptionAvailability = () => {
        if (!optionsWrap) return;
        optionsWrap.querySelectorAll('[data-mina-quickbuy-option-value]').forEach((button) => {
          const position = Number(button.dataset.position) - 1;
          const candidate = state.selectedOptions.slice();
          candidate[position] = button.dataset.value;
          const possible = state.variants.some((variant) =>
            variant.available && variant.options.every((value, index) => !candidate[index] || value === candidate[index]),
          );
          button.disabled = !possible;
        });
      };

      const updateVariant = ({ galleryChanged = false } = {}) => {
        state.activeVariant = resolveVariant();
        const active = state.activeVariant;
        if (active && price) {
          price.textContent = state.priceByVariant.get(String(active.id)) || price.textContent;
        }
        setBusy(false);
        updateOptionAvailability();
        if (detailsLink) {
          const url = new URL(state.productUrl, window.location.origin);
          if (active?.id) url.searchParams.set('variant', active.id);
          detailsLink.href = url.pathname + url.search;
        }
        if (galleryChanged) renderGallery(currentGalleryKey(), { animateFirst: true });
      };

      const buildOptions = () => {
        if (!optionsWrap) return;
        optionsWrap.replaceChildren();

        state.optionGroups.forEach((group) => {
          const fieldset = document.createElement('fieldset');
          fieldset.className = `mina-quickbuy__option${group.isColor ? ' mina-quickbuy__option--color' : ''}${group.isSize ? ' mina-quickbuy__option--size' : ''}`;

          const legend = document.createElement('legend');
          legend.className = 'mina-quickbuy__option-heading';
          const selectedValue = state.selectedOptions[group.position - 1];
          legend.textContent = group.isColor ? `${group.legend}: ${selectedValue || ''}` : group.legend;
          if (group.isSize && sizeChartButton) {
            fieldset.classList.add('mina-quickbuy__option--has-size-chart');
            legend.classList.add('mina-quickbuy__option-heading--with-action');
            legend.append(sizeChartButton);
          }

          fieldset.append(legend);

          const values = document.createElement('div');
          values.className = 'mina-quickbuy__option-values';

          group.values.forEach((value) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'mina-quickbuy__option-button';
            button.dataset.minaQuickbuyOptionValue = '';
            button.dataset.position = String(group.position);
            button.dataset.value = value.value;
            button.setAttribute('aria-label', `${group.legend}: ${value.value}`);

            if (group.isColor) {
              button.title = value.value;
              if (value.swatchImage) button.style.setProperty('--mqb-swatch-image', `url("${value.swatchImage}")`);
              if (value.swatchColor) button.style.setProperty('--mqb-swatch-color', value.swatchColor);
              const dot = document.createElement('span');
              dot.className = 'mina-quickbuy__swatch-dot';
              dot.setAttribute('aria-hidden', 'true');
              button.append(dot);
            } else {
              button.textContent = value.value;
            }

            const selected = selectedValue === value.value;
            button.classList.toggle('is-selected', selected);
            button.setAttribute('aria-pressed', String(selected));

            button.addEventListener('click', () => {
              if (button.disabled) return;
              const oldGallery = currentGalleryKey();
              state.selectedOptions[group.position - 1] = value.value;
              [...values.children].forEach((other) => {
                const isSelected = other === button;
                other.classList.toggle('is-selected', isSelected);
                other.setAttribute('aria-pressed', String(isSelected));
              });
              if (group.isColor) legend.textContent = `${group.legend}: ${value.value}`;
              const newGallery = currentGalleryKey();
              updateVariant({ galleryChanged: group.isColor && newGallery !== oldGallery });
            });

            values.append(button);
          });

          fieldset.append(values);
          optionsWrap.append(fieldset);
        });
      };

      const removeSizeChartClone = () => {
        if (!state.sizeChartClone) return;
        if (state.sizeChartClone.open && typeof state.sizeChartClone.close === 'function') state.sizeChartClone.close();
        state.sizeChartClone.remove();
        state.sizeChartClone = null;
      };

      const prepareSizeChart = (doc) => {
        removeSizeChartClone();
        if (!sizeChartButton) return;
        const source = doc.querySelector('[data-minaco-size-chart-dialog]');
        if (!source) {
          sizeChartButton.hidden = true;
          sizeChartButton.removeAttribute('data-minaco-size-chart-open');
          return;
        }

        const clone = source.cloneNode(true);
        const uniqueId = `MinacoQuickBuySizeChart-${section.id || Math.random().toString(36).slice(2)}`;
        clone.id = uniqueId;
        clone.dataset.minacoQuickbuyDynamic = 'true';
        clone.addEventListener('close', () => document.body.classList.remove('minaco-size-chart-open'));
        document.body.append(clone);
        state.sizeChartClone = clone;
        sizeChartButton.hidden = false;
        sizeChartButton.setAttribute('data-minaco-size-chart-open', uniqueId);
        sizeChartButton.setAttribute('aria-controls', uniqueId);
      };

      const populateQuickBuy = (doc, productUrl) => {
        const root = doc.querySelector('minaco-product[data-section-id], minaco-product, .minaco-product');
        if (!root) throw new Error('Product section was not found.');

        state.sourceDocument = doc;
        state.productRoot = root;
        state.productUrl = productUrl;
        state.optionGroups = extractOptionGroups(root);
        state.selectedOptions = [];
        state.optionGroups.forEach((group) => {
          const selected = group.values.find((value) => value.selected) || group.values[0];
          state.selectedOptions[group.position - 1] = selected?.value || '';
        });

        const variantsScript = root.querySelector('[data-minaco-variants]');
        state.variants = variantsScript ? JSON.parse(variantsScript.textContent || '[]') : [];
        state.priceByVariant = new Map(
          [...root.querySelectorAll('[data-minaco-variant-select] option')].map((option) => [
            option.value,
            option.dataset.price || '',
          ]),
        );

        if (title) title.textContent = root.querySelector('.minaco-product__title')?.textContent.trim() || '';
        if (price) price.textContent = root.querySelector('[data-minaco-product-price]')?.textContent.trim() || '';
        if (detailsLink) detailsLink.href = productUrl;
        if (saveButton) {
          saveButton.dataset.productId = root.dataset.productId || '';
          saveButton.dataset.productHandle = root.dataset.productHandle || '';
          saveButton.dataset.productTitle = root.dataset.productTitle || title?.textContent || '';
          saveButton.dataset.productUrl = root.dataset.productUrl || productUrl;
          saveButton.dataset.productImage = root.dataset.productImage || '';
          saveButton.dataset.productPrice = root.dataset.productPrice || price?.textContent || '';
          saveButton.dataset.productSubtitle = root.dataset.productSubtitle || '';
          if (window.MinacoAccount?.syncButton) window.MinacoAccount.syncButton(saveButton);
        }
        buildOptions();
        prepareSizeChart(doc);
        renderGallery(currentGalleryKey(), { animateFirst: false });
        updateVariant();
      };

      const openQuickBuy = async (opener) => {
        if (!window.matchMedia(DESKTOP_QUERY).matches) return;
        const productUrl = opener.dataset.productUrl || opener.getAttribute('href');
        if (!productUrl) return;

        state.opener = opener;
        state.requestToken += 1;
        const requestToken = state.requestToken;
        resetStatus();
        quickBuy.classList.add('is-loading');

        if (!quickBuy.open) {
          if (typeof quickBuy.showModal === 'function') quickBuy.showModal();
          else quickBuy.setAttribute('open', '');
          document.body.classList.add('mina-quickbuy-open');
          requestAnimationFrame(() => requestAnimationFrame(() => quickBuy.classList.add('is-visible')));
        }

        try {
          const doc = await loadProductDocument(productUrl);
          if (requestToken !== state.requestToken) return;
          populateQuickBuy(doc, productUrl);
          quickBuy.classList.remove('is-loading');
          requestAnimationFrame(() => closeQuickBuyButton?.focus({ preventScroll: true }));
        } catch (error) {
          console.error('[MINA QuickBuy]', error);
          quickBuy.classList.remove('is-loading');
          if (status) status.textContent = 'This product could not be loaded. Please view the product details.';
          if (detailsLink) detailsLink.href = productUrl;
        }
      };

      const closeQuickBuy = ({ returnFocus = true } = {}) => {
        if (!quickBuy.open) return;
        state.requestToken += 1;
        state.mediaAnimating = false;
        state.pendingMediaIndex = null;
        pauseFrames();
        quickBuy.classList.remove('is-visible');
        document.body.classList.remove('mina-quickbuy-open');
        window.setTimeout(() => {
          if (quickBuy.open) {
            if (typeof quickBuy.close === 'function') quickBuy.close();
            else quickBuy.removeAttribute('open');
          }
          stage?.replaceChildren();
          mediaNav?.replaceChildren();
          removeSizeChartClone();
          if (returnFocus) state.opener?.focus?.({ preventScroll: true });
        }, 280);
      };

      const addSelectedVariant = async () => {
        const variant = state.activeVariant;
        if (!variant?.id || !variant.available) throw new Error('Please select an available variant.');
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || 'Could not add this item to the cart.');
        }
        return response.json();
      };

      const purchase = async (mode) => {
        if (state.busy) return;
        resetStatus();
        setBusy(true);
        try {
          await addSelectedVariant();
          if (mode === 'checkout') {
            window.location.assign('/checkout');
            return;
          }

          if (window.MinacoCartDrawerController?.refresh) {
            await window.MinacoCartDrawerController.refresh();
          }
          closeQuickBuy({ returnFocus: false });
          window.setTimeout(() => {
            if (window.MinacoCartDrawerController?.open) {
              window.MinacoCartDrawerController.open(state.opener || addButton);
            }
          }, 300);
        } catch (error) {
          if (status) status.textContent = error.message || 'Could not add this item to the cart.';
          setBusy(false);
        }
      };

      section.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-mina-quickbuy-open]');
        if (!opener || !section.contains(opener)) return;
        if (!window.matchMedia(DESKTOP_QUERY).matches) return;
        event.preventDefault();
        openQuickBuy(opener);
      });

      closeQuickBuyButton?.addEventListener('click', () => closeQuickBuy());
      checkoutButton?.addEventListener('click', () => purchase('checkout'));
      addButton?.addEventListener('click', () => purchase('cart'));

      quickBuy.addEventListener('click', (event) => {
        if (event.target === quickBuy) closeQuickBuy();
      });

      quickBuy.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeQuickBuy();
      });

      quickBuy.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !quickBuy.open) return;
        const focusable = [...quickBuy.querySelectorAll(focusableSelector)].filter(
          (element) => !element.hidden && element.offsetParent !== null,
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-mina-collection]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
